import { Component, Fragment } from "react";

import { BTCheckbox } from "@btnet/core-components";

import { BdsButton } from "@buildertrend/components";

import { BTAlert } from "commonComponents/btWrappers/BTAlert/BTAlert";
import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTForm } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTLayoutContent } from "commonComponents/btWrappers/BTLayout/BTLayout";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTModalLayout } from "commonComponents/btWrappers/BTModal/BTModalLayout";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";

import { type JobPermissionResponse } from "entity/job/Job.api.types";

interface IPermissionWizardInternalProps {
    jobPermissions: JobPermissionResponse[];
    onSubmit: () => void;
    onCancel: () => void;
    onChange: (id: number, checked: boolean, index: number) => void;
    actionBeingPerformed?: string;
    modalConfig?: IModalConfiguration;
    fromBills?: boolean;
    useBds?: boolean;
}

class PermissionWizardInternal extends Component<IPermissionWizardInternalProps> {
    isAnyPermissionSelected() {
        const { jobPermissions } = this.props;
        for (const j of jobPermissions) {
            for (const permission of j.permissions) {
                if (permission.selected) return true;
                if (permission.nestedOptions) {
                    for (const nestedOption of permission.nestedOptions) {
                        if (nestedOption.selected) return true;
                    }
                }
            }
        }
        return false;
    }

    render() {
        const {
            jobPermissions,
            onChange,
            onSubmit,
            onCancel,
            actionBeingPerformed,
            modalConfig,
            fromBills,
            useBds,
        } = this.props;

        const isButtonDisabled = fromBills ? !this.isAnyPermissionSelected() : false;

        if (useBds) {
            const bdsFields = jobPermissions.map((j, index) =>
                j.permissions.map((permission) => {
                    if (permission.nestedOptions) {
                        const nestedFields = permission.nestedOptions.map((nestedOption) => (
                            <div className="flex flex-row" key={nestedOption.id}>
                                <div className="flex flex-col" style={{ paddingLeft: "16px" }}>
                                    <BTCheckbox
                                        id={`nested-permission-${nestedOption.id}`}
                                        data-testid={nestedOption.id.toString()}
                                        checked={nestedOption.selected}
                                        onChange={(e) =>
                                            onChange(nestedOption.id, e.target.checked, index)
                                        }
                                    >
                                        {nestedOption.formattedName}
                                    </BTCheckbox>
                                </div>
                            </div>
                        ));
                        return (
                            <Fragment key={permission.id}>
                                <div className="flex flex-row">
                                    <BTCheckbox
                                        id={`permission-${permission.id}`}
                                        data-testid={permission.id.toString()}
                                        checked={permission.selected}
                                        onChange={(e) =>
                                            onChange(permission.id, e.target.checked, index)
                                        }
                                    >
                                        {permission.formattedName}
                                    </BTCheckbox>
                                </div>
                                {nestedFields}
                            </Fragment>
                        );
                    } else {
                        return (
                            <div className="flex flex-row" key={permission.id}>
                                <BTCheckbox
                                    id={`permission-${permission.id}`}
                                    data-testid={permission.id.toString()}
                                    checked={permission.selected}
                                    onChange={(e) =>
                                        onChange(permission.id, e.target.checked, index)
                                    }
                                >
                                    {permission.formattedName}
                                </BTCheckbox>
                            </div>
                        );
                    }
                }),
            );
            return (
                <BTModalLayout
                    title="Permission Wizard"
                    footerContent={
                        <>
                            <BdsButton
                                displayType="primary"
                                testid="permisisonWizardUpdate"
                                text="Update Permissions"
                                onClick={onSubmit}
                                disabled={isButtonDisabled}
                            />
                            <BdsButton
                                displayType="secondary"
                                testid="permisisonWizardCancel"
                                text="Do Not Update Permissions"
                                onClick={onCancel}
                            />
                        </>
                    }
                    modalConfig={modalConfig}
                    closable={false}
                >
                    <BTForm data-testid="permission-wizard-form">
                        <BTLayoutContent>
                            {jobPermissions.map((perms, index) => (
                                <Fragment key={`section${index}`}>
                                    <BTAlert
                                        message={perms.headerMessage}
                                        showIcon
                                        type="success"
                                        data-testid="headerMessage"
                                    />
                                    <PageSection title={perms.titleText}>
                                        <p style={{ fontWeight: 600 }}>{perms.subTitleText}</p>
                                        {bdsFields[index]}
                                    </PageSection>
                                </Fragment>
                            ))}
                        </BTLayoutContent>
                    </BTForm>
                </BTModalLayout>
            );
        }

        const fields = jobPermissions.map((j, index) =>
            j.permissions.map((permission) => {
                if (permission.nestedOptions) {
                    const nestedFields = permission.nestedOptions.map((nestedOption) => {
                        return (
                            <BTRow key={nestedOption.id}>
                                <BTCol offset={1}>
                                    <BTCheckbox
                                        id={nestedOption.id.toString()}
                                        data-testid={nestedOption.id.toString()}
                                        checked={nestedOption.selected}
                                        key={nestedOption.id}
                                        onChange={(e) =>
                                            onChange(nestedOption.id, e.target.checked, index)
                                        }
                                    >
                                        {nestedOption.formattedName}
                                    </BTCheckbox>
                                </BTCol>
                            </BTRow>
                        );
                    });
                    return (
                        <Fragment key={permission.id}>
                            <BTRow>
                                <BTCheckbox
                                    id={permission.id.toString()}
                                    data-testid={permission.id.toString()}
                                    checked={permission.selected}
                                    key={permission.id}
                                    onChange={(e) =>
                                        onChange(permission.id, e.target.checked, index)
                                    }
                                >
                                    {permission.formattedName}
                                </BTCheckbox>
                            </BTRow>
                            {nestedFields}
                        </Fragment>
                    );
                } else {
                    return (
                        <BTRow key={permission.id}>
                            <BTCheckbox
                                id={permission.id.toString()}
                                data-testid={permission.id.toString()}
                                checked={permission.selected}
                                key={permission.id}
                                onChange={(e) => onChange(permission.id, e.target.checked, index)}
                            >
                                {permission.formattedName}
                            </BTCheckbox>
                        </BTRow>
                    );
                }
            }),
        );

        return (
            <BTModalLayout
                title="Permission Wizard"
                footerContent={
                    <>
                        <BTButtonDeprecated
                            type="primary"
                            data-testid="permisisonWizardUpdate"
                            loadingAction="save"
                            actionBeingPerformed={actionBeingPerformed}
                            onClick={onSubmit}
                            disabled={isButtonDisabled}
                        >
                            Update Permissions
                        </BTButtonDeprecated>
                        <BTButtonDeprecated
                            type="secondary"
                            data-testid="permisisonWizardCancel"
                            loadingAction="cancel"
                            actionBeingPerformed={actionBeingPerformed}
                            onClick={onCancel}
                        >
                            Do Not Update Permissions
                        </BTButtonDeprecated>
                    </>
                }
                modalConfig={modalConfig}
                closable={false}
            >
                <BTForm data-testid="permission-wizard-form">
                    <BTLayoutContent>
                        {jobPermissions.map((perms, index) => (
                            <Fragment key={`section${index}`}>
                                <BTAlert
                                    message={perms.headerMessage}
                                    showIcon
                                    type="success"
                                    data-testid="headerMessage"
                                />
                                <PageSection title={perms.titleText}>
                                    <p style={{ fontWeight: 600 }}>{perms.subTitleText}</p>
                                    {fields[index]}
                                </PageSection>
                            </Fragment>
                        ))}
                    </BTLayoutContent>
                </BTForm>
            </BTModalLayout>
        );
    }
}

export default PermissionWizardInternal;
